import { AlertCircle, AlertTriangle, Info, Bell } from 'lucide-react'

interface AlertsSummaryProps {
  data: {
    critical: number
    warning: number
    info: number
    unread: number
  }
  loading?: boolean
}

export function AlertsSummary({ data, loading = false }: AlertsSummaryProps) {
  const alertTypes = [
    {
      label: 'Críticos',
      value: data.critical,
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-200',
      severity: 'critical',
    },
    {
      label: 'Avisos',
      value: data.warning,
      icon: AlertTriangle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-200',
      severity: 'warning',
    },
    {
      label: 'Informações',
      value: data.info,
      icon: Info,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-200',
      severity: 'info',
    },
  ]

  const totalAlerts = data.critical + data.warning + data.info

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="h-4 bg-gray-200 rounded w-32 mb-4 animate-pulse" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Alertas Ativos</h3>
            <p className="text-sm text-gray-500">
              {totalAlerts} {totalAlerts === 1 ? 'alerta' : 'alertas'} ativos
              {data.unread > 0 && (
                <span className="text-teal-600 font-medium"> · {data.unread} não lidos</span>
              )}
            </p>
          </div>
        </div>
        <a
          href="/super-admin/alerts"
          className="text-sm text-teal-600 hover:text-teal-700 font-medium"
        >
          Ver todos
        </a>
      </div>

      {totalAlerts === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-gray-600 font-medium">Nenhum alerta ativo</p>
          <p className="text-sm text-gray-400 mt-1">A plataforma está funcionando normalmente</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alertTypes.map((alertType) => (
            <a
              key={alertType.severity}
              href={`/super-admin/alerts?severity=${alertType.severity}`}
              className={`flex items-center gap-4 p-4 rounded-lg border ${alertType.borderColor} hover:shadow-sm transition-shadow block ${
                alertType.value === 0 ? 'opacity-50' : ''
              }`}
            >
              <div className={`w-10 h-10 rounded-lg ${alertType.bgColor} flex items-center justify-center`}>
                <alertType.icon className={`w-5 h-5 ${alertType.color}`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{alertType.label}</p>
                <p className="text-sm text-gray-500">
                  {alertType.value} {alertType.value === 1 ? 'alerta' : 'alertas'}
                </p>
              </div>
              <div className={`text-2xl font-bold ${alertType.color}`}>{alertType.value}</div>
            </a>
          ))}
        </div>
      )}

      {data.critical > 0 && (
        <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm font-medium text-red-800">
              Atenção: {data.critical} {data.critical === 1 ? 'alerta crítico requer' : 'alertas críticos requerem'} ação imediata
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
